<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  ackMarketingPkFirework,
  getPendingMarketingPkFireworks,
  type MarketingPkFireworkEvent,
} from '@/api/modules/marketingPk'
import {
  ackTargetCelebration,
  claimTargetCelebration,
  type TargetCelebrationClaim,
  type TargetCelebrationItem,
} from '@/api/modules/marketingTarget'
import { formatPkValue, pkMetrics } from '@/pages/marketing-pk/presentation'
import { targetCelebrationDefaultImageUrls } from '@/pages/marketing-targets/targetPresentation'
import { useAuthStore } from '@/stores/auth'
import CelebrationPosterOverlay from './CelebrationPosterOverlay.vue'

type ActiveCelebration =
  | { kind: 'pk'; event: MarketingPkFireworkEvent }
  | { kind: 'target'; claim: TargetCelebrationClaim; item: TargetCelebrationItem }

const auth = useAuthStore()
const router = useRouter()
const active = ref<ActiveCelebration | null>(null)
const targetPermission = computed(() =>
  ['view', 'manage', 'settle'].some((action) =>
    auth.hasPermission(`client:marketing_target:${action}`),
  ),
)
const scope = computed(() =>
  JSON.stringify([
    Boolean(auth.token),
    auth.userInfo?.companyId,
    auth.userInfo?.id,
    auth.privateCompanyCode,
    auth.endpointProfileInfo,
  ]),
)
const pendingPkAcks = new Map<string, MarketingPkFireworkEvent>()
const pendingTargetAcks = new Map<number, TargetCelebrationClaim>()
const seenPkEvents = new Set<string>()
const seenTargetClaims = new Map<number, number>()
const BASE_POLL_INTERVAL_MS = 60_000
const MAX_POLL_INTERVAL_MS = 120_000
let generation = 0
let mounted = false
let busy = false
let pollAgain = false
let emptyPolls = 0
let failedPolls = 0
let pollIntervalMs = BASE_POLL_INTERVAL_MS
let timer: ReturnType<typeof setTimeout> | undefined

/** 返回当前目标喜报的海报标题；新快照只使用冻结主体名称，历史快照保留原文案降级。 */
const headline = computed(() => {
  if (!active.value) return ''
  if (active.value.kind === 'pk') {
    return active.value.event.teamName
      ? `恭喜${active.value.event.teamName}获得胜利`
      : '恭喜参赛队伍获得胜利'
  }
  const item = active.value.item
  if (item.subjectName) return `恭喜${item.subjectName}获得胜利`
  return item.title || '恭喜达成目标'
})

/** 返回当前海报主题；基础达成使用白金，PK 和冲刺均使用红金。 */
const theme = computed(() => {
  if (active.value?.kind === 'target' && active.value.item.level === 'base') return 'white-gold'
  return 'red-gold'
})

/** 返回当前舞台背景；使用已授权 HTTPS 默认资源，避免自定义图失效阻断系统通知。 */
const backgroundUrl = computed(() =>
  theme.value === 'white-gold'
    ? targetCelebrationDefaultImageUrls.base
    : targetCelebrationDefaultImageUrls.sprint,
)

/** 返回海报阶段标题；PK 使用冻结阶段，目标优先使用新冻结字段。 */
const stageTitle = computed(() => {
  if (!active.value) return ''
  if (active.value.kind === 'pk') return active.value.event.stageTitle || 'PK 活动达成'
  return (
    active.value.item.stageTitle ||
    (active.value.item.level === 'sprint' ? '冲刺目标达成' : '基础目标达成')
  )
})

/** 返回海报第二行对象/活动名称；不读取实时组织或进度数据。 */
const subjectName = computed(() => {
  if (!active.value) return ''
  if (active.value.kind === 'pk') return active.value.event.activityName || 'PK 活动'
  return active.value.item.targetName || active.value.item.name || '目标达成'
})

/** 返回冻结指标标签；历史目标缺字段时留空，不伪造数据。 */
const metricLabel = computed(() => {
  if (!active.value) return ''
  if (active.value.kind === 'pk') {
    return pkMetrics[active.value.event.metricKey]?.name || '当前战绩'
  }
  return active.value.item.metricLabel || ''
})

/** 返回冻结展示数值；历史目标缺字段时隐藏数值卡，避免伪造业绩。 */
const displayValue = computed(() => {
  if (!active.value) return ''
  if (active.value.kind === 'pk') {
    return formatPkValue(active.value.event.achievedValue, active.value.event.metricKey)
  }
  return active.value.item.displayValue || ''
})

/** 返回本次海报的时长；PK 保留后端 5/10/15 秒，目标固定十秒。 */
const duration = computed(() =>
  active.value?.kind === 'pk' ? active.value.event.durationSeconds : 10,
)

/** 返回主操作文本；PK 跳详情，目标跳结算结果页。 */
const primaryText = computed(() => (active.value?.kind === 'pk' ? '查看本场战况' : '查看本次战况'))

/** 返回 PK 声音开关；目标庆祝始终静音。 */
const hasSound = computed(() => active.value?.kind === 'pk' && active.value.event.hasSound)

/** 确认待 ACK 的领取记录；sequence/identity 防止旧身份异步写入。 */
async function flushPendingAcks(sequence: number, identity: string) {
  for (const [eventId, event] of pendingPkAcks) {
    if (sequence !== generation || identity !== scope.value) return
    await ackMarketingPkFirework(eventId, event.claimToken)
    if (sequence !== generation || identity !== scope.value) return
    pendingPkAcks.delete(eventId)
  }
  for (const [id, claim] of pendingTargetAcks) {
    if (sequence !== generation || identity !== scope.value) return
    if (Date.parse(claim.expiresAt) <= Date.now()) {
      pendingTargetAcks.delete(id)
      continue
    }
    await ackTargetCelebration(id, claim.claimToken)
    if (sequence !== generation || identity !== scope.value) return
    pendingTargetAcks.delete(id)
  }
}

/** 清理过期的本地去重标记；无参数，只维护当前身份的内存状态。 */
function clearExpiredSeen() {
  for (const [id, expiresAt] of seenTargetClaims) {
    if (expiresAt <= Date.now()) seenTargetClaims.delete(id)
  }
}

/** 清除唯一的轮询定时器；无参数，隐藏、身份切换和卸载时共用。 */
function clearPollTimer() {
  if (timer !== undefined) clearTimeout(timer)
  timer = undefined
}

/** 按本轮结果更新退避；result 区分空结果、收到通知和失败，间隔依次为 60/90/120 秒。 */
function updatePollInterval(result: 'empty' | 'received' | 'failed') {
  emptyPolls = result === 'empty' ? Math.min(emptyPolls + 1, 3) : 0
  failedPolls = result === 'failed' ? Math.min(failedPolls + 1, 3) : 0
  const streak = Math.max(emptyPolls, failedPolls)
  pollIntervalMs = Math.min(
    BASE_POLL_INTERVAL_MS + Math.max(0, streak - 1) * 30_000,
    MAX_POLL_INTERVAL_MS,
  )
}

/** 按当前间隔安排唯一的下一轮查询；无参数，待 ACK 重试不受超过 60 秒的退避影响。 */
function scheduleNextPoll() {
  clearPollTimer()
  if (!mounted || busy || !auth.token || !auth.userInfo || document.hidden) return
  const hasPendingAcks = pendingPkAcks.size > 0 || pendingTargetAcks.size > 0
  if (active.value && !hasPendingAcks) return
  timer = setTimeout(
    () => {
      timer = undefined
      void tick()
    },
    hasPendingAcks ? Math.min(pollIntervalMs, BASE_POLL_INTERVAL_MS) : pollIntervalMs,
  )
}

/** 串行 ACK 并优先领取 PK；queueIfBusy 供关闭回调登记一次续领，隐藏时仅处理 ACK。 */
async function tick(queueIfBusy = false) {
  if (!mounted || !auth.token || !auth.userInfo) return
  if (document.hidden && !queueIfBusy) return
  if (busy) {
    if (queueIfBusy) pollAgain = true
    return
  }
  clearPollTimer()
  const sequence = generation
  const identity = scope.value
  busy = true
  try {
    await flushPendingAcks(sequence, identity)
    if (sequence !== generation || identity !== scope.value || active.value || document.hidden)
      return
    clearExpiredSeen()
    const pkEvent = await getPendingMarketingPkFireworks()
    if (sequence !== generation || identity !== scope.value || active.value) return
    if (pkEvent?.eventId && pkEvent.claimToken) {
      updatePollInterval('received')
      if (seenPkEvents.has(pkEvent.eventId)) {
        pendingPkAcks.set(pkEvent.eventId, pkEvent)
        return
      }
      active.value = { kind: 'pk', event: pkEvent }
      return
    }
    if (document.hidden) return
    if (!targetPermission.value) {
      updatePollInterval('empty')
      return
    }
    const targetClaim = await claimTargetCelebration()
    if (sequence !== generation || identity !== scope.value || active.value) return
    if (!targetClaim || Date.parse(targetClaim.expiresAt) <= Date.now()) {
      updatePollInterval('empty')
      return
    }
    updatePollInterval('received')
    if (seenTargetClaims.has(targetClaim.id)) {
      pendingTargetAcks.set(targetClaim.id, targetClaim)
      return
    }
    const item = targetClaim.items[0]
    if (!item) {
      pendingTargetAcks.set(targetClaim.id, targetClaim)
      return
    }
    active.value = { kind: 'target', claim: targetClaim, item }
  } catch {
    /* 非核心通知失败静默退避；旧身份请求不得改变当前调度状态。 */
    if (sequence === generation && identity === scope.value) updatePollInterval('failed')
  } finally {
    if (sequence === generation && identity === scope.value) {
      busy = false
      const shouldPollAgain = pollAgain
      pollAgain = false
      if (shouldPollAgain) void tick(true)
      else scheduleNextPoll()
    }
  }
}

/** 关闭当前海报并登记 ACK；参数 navigate 指示主按钮后是否继续执行路由跳转。 */
async function finishActive(navigate = false) {
  const current = active.value
  active.value = null
  if (!current) return
  if (current.kind === 'pk') {
    seenPkEvents.add(current.event.eventId)
    if (seenPkEvents.size > 256) seenPkEvents.delete(seenPkEvents.values().next().value!)
    pendingPkAcks.set(current.event.eventId, current.event)
  } else {
    seenTargetClaims.set(current.claim.id, Date.parse(current.claim.expiresAt))
    pendingTargetAcks.set(current.claim.id, current.claim)
  }
  const sequence = generation
  const identity = scope.value
  // ACK 与后续领取共用串行入口；失败保留队列，不阻塞用户跳转。
  void tick(true)
  if (navigate && sequence === generation && identity === scope.value) {
    if (current.kind === 'pk') {
      await router.push(`/marketing/pk/detail/${current.event.activityId}`)
    } else {
      await router.push(`/marketing/targets/${current.claim.targetId}/result`)
    }
  }
}

/** 身份、租户或目标权限变化时清除旧租约；不可向旧身份发送 ACK。 */
function reset() {
  clearPollTimer()
  generation += 1
  busy = false
  pollAgain = false
  updatePollInterval('received')
  active.value = null
  pendingPkAcks.clear()
  pendingTargetAcks.clear()
  seenPkEvents.clear()
  seenTargetClaims.clear()
  void tick()
}

/** 页面隐藏时停止调度，恢复可见时补查；无参数，在途查询可复用，不额外并发。 */
function handleVisibilityChange() {
  if (document.hidden) {
    clearPollTimer()
    return
  }
  void tick()
}

watch([scope, targetPermission], reset)
onMounted(() => {
  mounted = true
  void tick()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})
onBeforeUnmount(() => {
  mounted = false
  generation += 1
  pollAgain = false
  clearPollTimer()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  active.value = null
  pendingPkAcks.clear()
  pendingTargetAcks.clear()
})
</script>

<template>
  <CelebrationPosterOverlay
    v-if="active"
    :show="true"
    :theme="theme"
    :background-url="backgroundUrl"
    slogan="荣耀时刻"
    :headline="headline"
    :stage-title="stageTitle"
    :subject-name="subjectName"
    :metric-label="metricLabel"
    :display-value="displayValue"
    :primary-text="primaryText"
    :duration="duration"
    :has-sound="hasSound"
    @close="void finishActive()"
    @primary="void finishActive(true)"
  />
</template>
